import { endOfDay, formatDistanceToNow, isAfter, parseISO } from 'date-fns';
import type {
  Invoice,
  InvoiceBadgeStatus,
  InvoiceDisplayStatus,
  InvoiceDisplayStatusFilter,
  InvoiceStatus,
} from '@/types/invoice';

// Wave 1 — Spec §2.2 collapses SENT/VIEWED/OVERDUE into OPEN. The legacy
// lowercase keys (`sent`, `viewed`, `overdue`) still appear here so that
// pre-Wave-1 API responses keep normalizing while staging rolls forward;
// they all funnel into 'open' in the canonical map.
const CLIENT_STATUS_BY_API_STATUS: Record<string, InvoiceStatus> = {
  draft: 'draft',
  open: 'open',
  sent: 'open',
  viewed: 'open',
  overdue: 'open',
  partially_paid: 'partial',
  partial: 'partial',
  paid: 'paid',
  void: 'void',
  written_off: 'written_off',
  writtenoff: 'written_off',
};

// Reverse map to API. Legacy 'sent'/'viewed'/'overdue' keys still resolve
// (back to OPEN) so any cached UI state continues to round-trip cleanly.
const API_STATUS_BY_CLIENT_STATUS: Record<InvoiceStatus, string> = {
  draft: 'DRAFT',
  open: 'OPEN',
  sent: 'OPEN',
  viewed: 'OPEN',
  partial: 'PARTIALLY_PAID',
  paid: 'PAID',
  overdue: 'OPEN',
  void: 'VOID',
  written_off: 'WRITTEN_OFF',
};

const STATUS_LABEL_BY_CLIENT_STATUS: Record<string, string> = {
  draft: 'Draft',
  open: 'Open',
  sent: 'Sent',
  viewed: 'Viewed',
  partial: 'Partially Paid',
  paid: 'Paid',
  overdue: 'Overdue', // computed from dueDate < now, badge label still shown
  void: 'Void',
  written_off: 'Written Off',
  refunded: 'Refunded',
  clearing: 'Clearing',
  // A list-filter label only: an unpaid invoice's badge reads Open.
  unpaid: 'Unpaid',
};

export function normalizeInvoiceStatus(value: unknown): InvoiceStatus {
  const normalized = String(value ?? 'draft')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');

  return CLIENT_STATUS_BY_API_STATUS[normalized] ?? 'draft';
}

export function toInvoiceStatusApiParam(status?: InvoiceStatus | '') {
  if (!status) return undefined;
  return API_STATUS_BY_CLIENT_STATUS[status];
}

export function formatInvoiceStatusLabel(status: InvoiceStatus | string) {
  const normalized = String(status ?? 'draft')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');

  if (STATUS_LABEL_BY_CLIENT_STATUS[normalized]) {
    return STATUS_LABEL_BY_CLIENT_STATUS[normalized];
  }

  return STATUS_LABEL_BY_CLIENT_STATUS[normalizeInvoiceStatus(status)];
}

export function computeEffectiveInvoiceStatus(invoice: Invoice): InvoiceStatus {
  // Field-masked responses intentionally omit balanceDue. A missing balance must
  // not make the same overdue invoice appear Open for restricted roles. Explicit
  // zero still means there is nothing outstanding, so it remains non-overdue.
  const balanceWasOmitted = invoice.financialFieldPresence?.balanceDue === false;
  const canDeriveOmittedBalance =
    balanceWasOmitted &&
    invoice.financialFieldPresence?.total === true &&
    invoice.financialFieldPresence?.amountPaid === true;
  const derivedBalance = canDeriveOmittedBalance
    ? Math.max(0, Number(invoice.total) - Number(invoice.amountPaid))
    : undefined;
  const hasOutstandingBalance =
    derivedBalance !== undefined
      ? derivedBalance > 0
      : balanceWasOmitted || invoice.balanceDue == null || Number(invoice.balanceDue) > 0;
  const canBecomeOverdue = invoice.status === 'open' || invoice.status === 'partial';

  if (
    canBecomeOverdue &&
    hasOutstandingBalance &&
    invoice.dueDate &&
    isAfter(new Date(), endOfDay(parseISO(invoice.dueDate)))
  ) {
    return 'overdue';
  }
  return invoice.status;
}

const BADGE_STATUS_BY_DISPLAY_STATUS: Record<InvoiceDisplayStatus, InvoiceBadgeStatus> = {
  DRAFT: 'draft',
  OPEN: 'open',
  PARTIALLY_PAID: 'partial',
  CLEARING: 'clearing',
  OVERDUE: 'overdue',
  PAID: 'paid',
  VOID: 'void',
  WRITTEN_OFF: 'written_off',
};

const OPEN_CLIENT_STATUSES: ReadonlySet<InvoiceStatus> = new Set([
  'open',
  'sent',
  'viewed',
  'overdue',
]);
const OWING_CLIENT_STATUSES: ReadonlySet<InvoiceStatus> = new Set([
  ...OPEN_CLIENT_STATUSES,
  'partial',
]);

/**
 * The stored statuses a display status can be derived from (operations
 * `deriveInvoiceDisplayStatus`). Anything else is a display status left over
 * from before a mutation moved the stored status on. A stored OPEN invoice may
 * read Partially paid, and a stored PAID one Clearing, while bank money clears
 * (spec 2026-09-28).
 */
const STORED_STATUSES_BY_DISPLAY_STATUS: Record<
  InvoiceDisplayStatus,
  ReadonlySet<InvoiceStatus>
> = {
  DRAFT: new Set(['draft']),
  OPEN: OPEN_CLIENT_STATUSES,
  // Stored OPEN with bank money clearing on part of it reads Partially paid.
  PARTIALLY_PAID: new Set(['partial', ...OPEN_CLIENT_STATUSES]),
  // Stored PAID on provisional bank money reads Clearing until it settles.
  CLEARING: new Set([...OWING_CLIENT_STATUSES, 'paid']),
  OVERDUE: OWING_CLIENT_STATUSES,
  PAID: new Set(['paid']),
  VOID: new Set(['void']),
  WRITTEN_OFF: new Set(['written_off']),
};

/**
 * The server's `displayStatus`, when it can still be believed: a known value
 * that agrees with the stored status beside it. Send, record payment and
 * write-off merge their responses into the cached invoice, and a response
 * without a fresh `displayStatus` leaves the old one next to the new status.
 */
function currentDisplayStatus(invoice: Invoice): InvoiceDisplayStatus | null {
  const display = invoice.displayStatus;
  if (
    !display ||
    !Object.prototype.hasOwnProperty.call(STORED_STATUSES_BY_DISPLAY_STATUS, display)
  ) {
    return null;
  }
  return STORED_STATUSES_BY_DISPLAY_STATUS[display].has(invoice.status) ? display : null;
}

/**
 * The badge an invoice shows. The server's `displayStatus` wins — it alone
 * knows whether a bank payment is clearing, and it applies the same Overdue
 * rule as the Overdue filter — unless it is stale (see `currentDisplayStatus`).
 * Display only: action gating keeps reading `computeEffectiveInvoiceStatus`,
 * which Clearing does not change.
 */
export function computeInvoiceBadgeStatus(invoice: Invoice): InvoiceBadgeStatus {
  const display = currentDisplayStatus(invoice);
  if (display) return BADGE_STATUS_BY_DISPLAY_STATUS[display];
  return computeEffectiveInvoiceStatus(invoice);
}

/** One list-filter value → the API params that select it. Filters are additive. */
export function splitInvoiceStatusFilter(value: InvoiceStatus | 'all' | 'clearing' | 'unpaid'): {
  status?: InvoiceStatus;
  overdue?: true;
  displayStatus?: InvoiceDisplayStatusFilter;
} {
  if (value === 'all') return {};
  if (value === 'overdue') return { overdue: true };
  if (value === 'clearing') return { displayStatus: 'CLEARING' };
  if (value === 'unpaid') return { displayStatus: 'UNPAID' };
  // Derived, so it lists exactly what the badge calls Partially paid.
  if (value === 'partial') return { displayStatus: 'PARTIALLY_PAID' };
  return { status: value };
}

/**
 * "How long" for the header beside the badge, keyed on the BADGE status so the
 * clock matches the word: Overdue counts from the due date, Clearing from when
 * the payment was submitted (never from the due date), Paid from payment.
 *
 * Clearing is shown to every actor, but when the money was submitted is a
 * clearing figure, withheld where the balance is. Without it the header says
 * no time at all: any other date would claim the payment is that old.
 */
export function formatRelativeInvoiceStatusTime(
  invoice: Invoice,
  badgeStatus: InvoiceBadgeStatus,
): string {
  const source =
    badgeStatus === 'paid'
      ? invoice.paidAt
      : badgeStatus === 'overdue'
        ? invoice.dueDate
        : badgeStatus === 'clearing'
          ? (invoice.clearingSince ?? invoice.settlingPayment?.since)
          : (invoice.viewedAt ?? invoice.sentAt ?? invoice.date ?? invoice.createdAt);
  if (!source) return '';
  const date = new Date(source);
  if (Number.isNaN(date.getTime())) return '';
  return formatDistanceToNow(date, { addSuffix: true });
}
